import { useState, useEffect, useMemo } from 'react';
import { sortByName } from '../lib/sortByName';
import { useTranslation } from 'react-i18next';
import { employeesAPI, locationsAPI } from '../lib/api';
import api from '../lib/api';
import { useAuth } from '../lib/auth';
import { checkPhone } from '../lib/phone';
import { toast } from 'sonner';
import {
    Users,
    Search,
    Plus,
    Mail,
    Phone,
    MapPin,
    MoreVertical,
    Edit2,
    Trash2,
    Wifi,
    Lock,
    KeyRound
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { Label } from '../components/ui/label';
import { EmptyState, HelpTooltip } from '../components/UsabilityComponents';
import { serializeEmployeeSettings } from '../utils/serializeEmployeeSettings';
import { MyTapAccessModal } from '../components/mytap/MyTapAccessModal';
import { SetupGuidanceCard } from '../components/setup_guide/SetupGuidanceCard';
import { HelpGlyph } from '../components/setup_guide/HelpGlyph';

const Employees = () => {
    const { t } = useTranslation();
    const [employees, setEmployees] = useState([]);
    const [locations, setLocations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterLocation, setFilterLocation] = useState('all');
    
    // Modal states
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [phoneError, setPhoneError] = useState(null);
    const [editingEmployee, setEditingEmployee] = useState(null);
    const HOTEL_CAPS = ['housekeeping', 'security', 'concierge', 'valet', 'hotel_manager'];
    const [capsDraft, setCapsDraft] = useState([]);

    // PIN modal states
    const [isPinModalOpen, setIsPinModalOpen] = useState(false);
    const [pinTarget, setPinTarget] = useState(null);
    const [pinValue, setPinValue] = useState('');
    const [pinConfirm, setPinConfirm] = useState('');
    const [pinSaving, setPinSaving] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        employee_code: '',
        email: '',
        phone: '',
        location_id: '',
        person_type: 'employee',
        unit_number: ''
    });

    // v4.9.0 — MyTap access modal
    const [mytapEmployee, setMytapEmployee] = useState(null);

    // Single-source-of-truth dirty detection (see /app/frontend/src/utils/serializeEmployeeSettings.js).
    const [employeeSettingsSnapshot, setEmployeeSettingsSnapshot] = useState(null);

    const { user, canManageEmployees, isSuperAdmin, isClientAdmin } = useAuth();

    const isEmployeeDirty = useMemo(() => {
        if (!employeeSettingsSnapshot) return false;
        const current = serializeEmployeeSettings(formData, 'state');
        return current !== employeeSettingsSnapshot;
    }, [formData, employeeSettingsSnapshot]);

    const isEditingEmployee = Boolean(editingEmployee?.id);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            const [employeesRes, locationsRes] = await Promise.all([
                employeesAPI.list(),
                locationsAPI.list()
            ]);
            setEmployees(sortByName(employeesRes.data));
            setLocations(sortByName(locationsRes.data));
        } catch (error) {
            toast.error('Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    const openModal = (employee = null) => {
        if (employee) {
            setEditingEmployee(employee);
            const next = {
                name: employee.name,
                employee_code: employee.employee_code || '',
                email: employee.email || '',
                phone: employee.phone || '',
                location_id: employee.location_id,
                person_type: employee.person_type || 'employee',
                unit_number: employee.unit_number || ''
            };
            setFormData(next);
            setCapsDraft(employee.hotel_capabilities || []);
            setEmployeeSettingsSnapshot(serializeEmployeeSettings(employee, 'employee'));
        } else {
            setEditingEmployee(null);
            const blank = {
                name: '',
                employee_code: '',
                email: '',
                phone: '',
                location_id: locations[0]?.id || '',
                person_type: 'employee',
                unit_number: ''
            };
            setFormData(blank);
            setEmployeeSettingsSnapshot(serializeEmployeeSettings(blank, 'state'));
        }
        setPhoneError(null);
        setIsModalOpen(true);
    };

    const handleSubmit = async () => {
        if (!formData.name.trim()) {
            toast.error('Please enter employee name');
            return;
        }
        if (!formData.location_id) {
            toast.error('Please select a location');
            return;
        }
        // Block the save on a number Twilio could never dial — the old code
        // stored it happily and then no notification ever arrived.
        const phoneCheck = await checkPhone(formData.phone);
        if (!phoneCheck.valid) {
            setPhoneError(t('phone.invalid', { value: formData.phone.trim() }));
            return;
        }
        setPhoneError(null);

        const location = locations.find(l => l.id === formData.location_id);
        const payload = {
            name: formData.name.trim(),
            employee_code: formData.employee_code.trim() || null,
            email: formData.email.trim() || null,
            phone: phoneCheck.e164,
            organization_id: location.organization_id,
            location_id: formData.location_id,
            person_type: formData.person_type || 'employee',
            unit_number: (formData.person_type === 'tenant' || formData.person_type === 'owner')
                ? (formData.unit_number.trim() || null) : null
        };

        try {
            if (editingEmployee) {
                await employeesAPI.update(editingEmployee.id, payload);
                const prevCaps = (editingEmployee.hotel_capabilities || []).slice().sort().join(',');
                if (prevCaps !== capsDraft.slice().sort().join(',')) {
                    await api.put(`/assistant/employees/${editingEmployee.id}/capabilities`,
                        { hotel_capabilities: capsDraft });
                }
                toast.success('Employee updated successfully');
            } else {
                await employeesAPI.create(payload);
                toast.success('Employee created successfully');
            }
            setIsModalOpen(false);
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Operation failed');
        }
    };

    const handleDelete = async (employeeId) => {
        if (!window.confirm('Are you sure you want to delete this employee? Any assigned cards will be unassigned.')) {
            return;
        }

        try {
            await employeesAPI.delete(employeeId);
            toast.success('Employee deleted');
            loadData();
        } catch (error) {
            toast.error('Failed to delete employee');
        }
    };

    const openPinModal = (employee) => {
        setPinTarget(employee);
        setPinValue('');
        setPinConfirm('');
        setIsPinModalOpen(true);
    };

    const handleSetPin = async () => {
        if (!pinTarget) return;
        if (pinValue.length !== 4 || !/^\d{4}$/.test(pinValue)) {
            toast.error('PIN must be exactly 4 digits');
            return;
        }
        if (pinValue !== pinConfirm) {
            toast.error('PINs do not match');
            return;
        }

        setPinSaving(true);
        try {
            const res = await employeesAPI.setPin(pinTarget.id, pinValue);
            toast.success(`PIN set for ${pinTarget.name}`);
            setIsPinModalOpen(false);
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || err.message || 'Failed to set PIN');
        } finally {
            setPinSaving(false);
        }
    };

    const handleRemovePin = async (employee) => {
        if (!window.confirm(`Remove PIN for ${employee.name}? They will need a new PIN set to authenticate.`)) return;
        try {
            await employeesAPI.removePin(employee.id);
            toast.success(`PIN removed for ${employee.name}`);
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || err.message || 'Failed to remove PIN');
        }
    };

    const getLocationName = (locationId) => {
        return locations.find(l => l.id === locationId)?.name || 'Unknown';
    };

    const filteredEmployees = employees.filter(emp => {
        const term = searchTerm.toLowerCase();
        const matchesSearch =
            emp.name.toLowerCase().includes(term) ||
            (emp.email && emp.email.toLowerCase().includes(term)) ||
            (emp.employee_code && emp.employee_code.toLowerCase().includes(term));
        const matchesLocation = filterLocation === 'all' || emp.location_id === filterLocation;
        return matchesSearch && matchesLocation;
    });

    if (loading) {
        return (
            <div className="space-y-6">
                <div className="skeleton h-20 rounded-xl" />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[1, 2, 3, 4, 5, 6].map((i) => (
                        <div key={i} className="skeleton h-48 rounded-xl" />
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="employees-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl sm:text-3xl font-bold text-foreground flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {t('employees.title')}
                        <HelpTooltip text={t('dashboard.employeesHelp')} />
                        <HelpGlyph guideKey="employees" testId="employees-help-glyph" />
                    </h1>
                    <p className="text-muted-foreground mt-1">{t('employees.subtitle')}
                        {' '}<a href="/card-guide#employee" className="text-xs text-amber-600 underline" data-testid="employees-card-guide-link">{t('cardGuide.employeeHelp', 'How do I assign a Tap Card to an employee?')}</a>
                    </p>
                </div>
                {canManageEmployees() && (
                    <Button 
                        onClick={() => openModal()}
                        className="bg-blue-600 hover:bg-blue-700"
                        data-testid="add-employee-btn"
                        data-insight-id="employees.action.add"
                        data-insight-label="Add Employee"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        {t('employees.addEmployee')}
                    </Button>
                )}
            </div>

            {/* v4.10.0 — Lightweight first-time guidance (dismissible, reopen via help glyph) */}
            <SetupGuidanceCard
                guideKey="employees"
                points={[
                    "setup_guide.employees.point_id",
                    "setup_guide.employees.point_card",
                    "setup_guide.employees.point_invite",
                ]}
                testId="setup-guide-employees"
            />

            {/* Filters */}
            <Card data-insight-id="employees.filters" data-insight-label="Employee filters">
                <CardContent className="p-4">
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                                placeholder={t('employees.searchPlaceholder')}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10"
                                data-testid="search-employees-input"
                            />
                        </div>
                        {locations.length > 1 && (
                            <Select value={filterLocation} onValueChange={setFilterLocation}>
                                <SelectTrigger className="w-full sm:w-48" data-testid="filter-location">
                                    <MapPin className="w-4 h-4 mr-2" />
                                    <SelectValue placeholder={t('cards.allLocations')} />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">{t('cards.allLocations')}</SelectItem>
                                    {locations.map(loc => (
                                        <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        )}
                    </div>
                </CardContent>
            </Card>

            {/* Employees Grid */}
            {filteredEmployees.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredEmployees.map((employee, idx) => (
                        <Card 
                            key={employee.id} 
                            className="card-hover"
                            data-testid={`employee-${employee.id}`}
                            data-insight-id="employees.card"
                            data-insight-label="Employee card"
                        >
                            <CardContent className="p-5">
                                <div className="flex items-start justify-between mb-4">
                                    <div className="flex items-center gap-3">
                                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-semibold text-lg">
                                            {employee.name.charAt(0).toUpperCase()}
                                        </div>
                                        <div>
                                            <p className="font-semibold text-foreground flex items-center gap-1.5 flex-wrap">
                                                {employee.name}
                                                {employee.employee_code && (
                                                    <Badge variant="secondary" className="text-[10px] px-1.5 py-0 font-mono" data-testid={`emp-code-${employee.id}`}>
                                                        {employee.employee_code}
                                                    </Badge>
                                                )}
                                                {employee.has_pin ? (
                                                    <Badge variant="secondary" className="text-[10px] px-1.5 py-0 gap-1 bg-green-600/15 text-green-700 dark:text-green-400" data-testid={`pin-indicator-${employee.id}`} title={t('employees.pinSet')}>
                                                        <Lock className="w-3 h-3" /> {t('employees.pinSetBadge', 'PIN set')}
                                                    </Badge>
                                                ) : (
                                                    <Badge variant="outline" className="text-[10px] px-1.5 py-0 gap-1 text-muted-foreground" data-testid={`pin-notset-${employee.id}`} title={t('employees.pinNotSet', 'PIN not set')}>
                                                        {t('employees.pinNotSetBadge', 'PIN not set')}
                                                    </Badge>
                                                )}
                                            </p>
                                            <Badge variant="outline" className="text-xs mt-1">
                                                <MapPin className="w-3 h-3 mr-1" />
                                                {getLocationName(employee.location_id)}
                                            </Badge>
                                            {(employee.person_type === 'tenant' || employee.person_type === 'owner') && (
                                                <Badge className="text-[10px] px-1.5 py-0 mt-1 ml-1 bg-violet-600/15 text-violet-700 dark:text-violet-300" data-testid={`person-type-badge-${employee.id}`}>
                                                    {employee.person_type === 'owner' ? t('employees.personTypeOwner', 'Owner') : t('employees.personTypeTenant', 'Tenant')}
                                                    {employee.unit_number ? ` · ${t('employees.unitNumber', 'Unit')} ${employee.unit_number}` : ''}
                                                </Badge>
                                            )}
                                        </div>
                                    </div>
                                    {canManageEmployees() && (
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-8 w-8 p-0"
                                                    data-insight-id={idx === 0 ? "employees.workflow.actions-menu" : undefined}
                                                    data-insight-label={idx === 0 ? "Employee actions menu" : undefined}
                                                >
                                                    <MoreVertical className="w-4 h-4" />
                                                </Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="end">
                                                <DropdownMenuItem onClick={() => openModal(employee)} data-testid={`emp-edit-${employee.id}`} data-insight-id={idx === 0 ? "employees.workflow.edit-profile" : undefined} data-insight-label={idx === 0 ? "Edit employee" : undefined}>
                                                    <Edit2 className="w-4 h-4 mr-2" />
                                                    Edit
                                                </DropdownMenuItem>
                                                <DropdownMenuItem onClick={() => setMytapEmployee(employee)} data-testid={`emp-mytap-${employee.id}`} data-insight-id={idx === 0 ? "employees.workflow.mytap-invite" : undefined} data-insight-label={idx === 0 ? "MyTap invitation" : undefined}>
                                                    <KeyRound className="w-4 h-4 mr-2" />
                                                    {t('mytap.access.menu_item')}
                                                </DropdownMenuItem>
                                                <DropdownMenuSeparator />
                                                <DropdownMenuItem onClick={() => openPinModal(employee)} data-testid={`emp-set-pin-${employee.id}`}>
                                                    <KeyRound className="w-4 h-4 mr-2" />
                                                    Set / Reset PIN
                                                </DropdownMenuItem>
                                                <DropdownMenuItem onClick={() => handleRemovePin(employee)} data-testid={`emp-remove-pin-${employee.id}`} data-insight-id={idx === 0 ? "employees.workflow.remove-pin" : undefined} data-insight-label={idx === 0 ? "Remove PIN" : undefined}>
                                                    <Lock className="w-4 h-4 mr-2" />
                                                    Remove PIN
                                                </DropdownMenuItem>
                                                <DropdownMenuSeparator />
                                                <DropdownMenuItem 
                                                    onClick={() => handleDelete(employee.id)}
                                                    className="text-red-600"
                                                    data-testid={`emp-delete-${employee.id}`}
                                                    data-insight-id={idx === 0 ? "employees.workflow.delete" : undefined}
                                                    data-insight-label={idx === 0 ? "Delete employee" : undefined}
                                                >
                                                    <Trash2 className="w-4 h-4 mr-2" />
                                                    Delete
                                                </DropdownMenuItem>
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    {employee.email && (
                                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                            <Mail className="w-4 h-4 text-muted-foreground" />
                                            <span className="truncate">{employee.email}</span>
                                        </div>
                                    )}
                                    {employee.phone && (
                                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                            <Phone className="w-4 h-4 text-muted-foreground" />
                                            <span>{employee.phone}</span>
                                        </div>
                                    )}
                                </div>

                                <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-700">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <Wifi className="w-4 h-4 text-blue-500" />
                                            <span className="text-sm text-muted-foreground">{t('employees.totalTaps')}</span>
                                        </div>
                                        <span className="text-lg font-semibold text-foreground">
                                            {employee.tap_count || 0}
                                        </span>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            ) : (
                <Card>
                    <CardContent className="py-16">
                        <EmptyState
                            icon={Users}
                            title={searchTerm || filterLocation !== 'all' ? t('empty.employeesFilterTitle', 'No employees found') : t('empty.employeesTitle', 'No team members yet')}
                            description={
                                searchTerm || filterLocation !== 'all'
                                    ? t('empty.employeesFilterDesc', 'Try adjusting your search or filter.')
                                    : t('empty.employeesDesc', 'Add your first team member to get started. After adding employees, you can assign them Tap Engine Cards.')
                            }
                            action={
                                canManageEmployees() && !searchTerm && filterLocation === 'all' && (
                                    <Button onClick={() => openModal()} data-testid="empty-add-employee-btn">
                                        <Plus className="w-4 h-4 mr-2" />
                                        {t('empty.employeesAction', 'Add Your First Team Member')}
                                    </Button>
                                )
                            }
                        />
                    </CardContent>
                </Card>
            )}

            {/* Create/Edit Modal */}
            <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                            {editingEmployee ? t('employees.editEmployee') : t('employees.addEmployee')}
                        </DialogTitle>
                    </DialogHeader>
                    <p className="text-xs text-muted-foreground">{t('common.requiredFields')}</p>
                    <div className="space-y-4 py-4">
                        <div>
                            <Label className="mb-2 block">{t('employees.name')} <span className="text-red-500">*</span></Label>
                            <Input
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                placeholder={t('employees.namePlaceholder')}
                                data-testid="employee-name-input"
                            />
                        </div>
                        <div>
                            <Label className="mb-2 block">{t('employees.personType', 'Person Type')} <span className="text-red-500">*</span></Label>
                            <Select
                                value={formData.person_type}
                                onValueChange={(val) => setFormData({ ...formData, person_type: val })}
                            >
                                <SelectTrigger data-testid="employee-person-type-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="employee" data-testid="person-type-employee">{t('employees.personTypeEmployee', 'Employee')}</SelectItem>
                                    <SelectItem value="tenant" data-testid="person-type-tenant">{t('employees.personTypeTenant', 'Tenant')}</SelectItem>
                                    <SelectItem value="owner" data-testid="person-type-owner">{t('employees.personTypeOwner', 'Owner')}</SelectItem>
                                </SelectContent>
                            </Select>
                            <p className="text-xs text-muted-foreground mt-1">{t('employees.personTypeHint', 'Employee = operational staff. Tenant / Owner = property resident.')}</p>
                        </div>
                        {(formData.person_type === 'tenant' || formData.person_type === 'owner') && (
                            <div data-testid="employee-unit-block">
                                <Label className="mb-2 block">{t('employees.unitNumber', 'Unit')} <span className="text-muted-foreground text-xs">{t('common.optionalParen')}</span></Label>
                                <Input
                                    value={formData.unit_number}
                                    onChange={(e) => setFormData({ ...formData, unit_number: e.target.value })}
                                    placeholder={t('employees.unitNumberPlaceholder', 'e.g. 101, 12B')}
                                    data-testid="employee-unit-input"
                                    maxLength={10}
                                />
                                <p className="text-xs text-muted-foreground mt-1">{t('employees.unitNumberHint', 'The building/unit this resident is permanently assigned to (set the building via Location below).')}</p>
                            </div>
                        )}
                        <div>
                            <Input
                                value={formData.employee_code}
                                onChange={(e) => setFormData({ ...formData, employee_code: e.target.value })}
                                placeholder={t('employees.employee_id_field.placeholder')}
                                data-testid="employee-code-input"
                                maxLength={32}
                            />
                            <p className="text-xs text-muted-foreground mt-1">{t('employees.employee_id_field.helper')}</p>
                        </div>
                        <div>
                            <Label className="mb-2 block">{t('employees.email')} <span className="text-muted-foreground text-xs">{t('common.optionalParen')}</span></Label>
                            <Input
                                type="email"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                placeholder="john@restaurant.com"
                                data-testid="employee-email-input"
                            />
                        </div>
                        <div>
                            <Label className="mb-2 block">{t('employees.phone')} <span className="text-muted-foreground text-xs">{t('common.optionalParen')}</span></Label>
                            <Input
                                value={formData.phone}
                                onChange={(e) => { setPhoneError(null); setFormData({ ...formData, phone: e.target.value }); }}
                                onBlur={async (e) => {
                                    const { valid } = await checkPhone(e.target.value);
                                    setPhoneError(valid ? null : t('phone.invalid', { value: e.target.value.trim() }));
                                }}
                                placeholder={t('employees.phonePlaceholder')}
                                className={phoneError ? 'border-red-500 focus-visible:ring-red-500' : ''}
                                data-testid="employee-phone-input"
                            />
                            {phoneError && (
                                <p className="mt-1 text-xs text-red-500" data-testid="employee-phone-error">{phoneError}</p>
                            )}
                        </div>
                        <div>
                            <Label className="mb-2 block">{t('employees.location')} <span className="text-red-500">*</span></Label>
                            <Select 
                                value={formData.location_id} 
                                onValueChange={(val) => setFormData({ ...formData, location_id: val })}
                            >
                                <SelectTrigger data-testid="employee-location-select">
                                    <SelectValue placeholder={t('employees.selectLocation')} />
                                </SelectTrigger>
                                <SelectContent>
                                    {locations.map(loc => (
                                        <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        {isEditingEmployee && formData.person_type === 'employee' && (
                            <div data-testid="employee-hotel-caps">
                                <Label className="mb-2 block">
                                    {t('assistant.staffCaps', 'Hotel functions (In-Room Assistant)')}
                                </Label>
                                <div className="grid grid-cols-2 gap-2">
                                    {HOTEL_CAPS.map((cap) => (
                                        <label key={cap} className="flex items-center gap-2 text-sm border rounded-lg p-2 cursor-pointer">
                                            <input type="checkbox" checked={capsDraft.includes(cap)}
                                                onChange={(e) => setCapsDraft(e.target.checked
                                                    ? [...capsDraft, cap]
                                                    : capsDraft.filter((c) => c !== cap))}
                                                data-testid={`employee-cap-${cap}`} />
                                            {t(`assistant.staff_${cap === 'hotel_manager' ? 'management' : cap}`,
                                                cap === 'hotel_manager' ? 'Management' : cap.charAt(0).toUpperCase() + cap.slice(1))}
                                        </label>
                                    ))}
                                </div>
                                <p className="text-xs text-muted-foreground mt-1">
                                    {t('assistant.staffHint', 'Which operational functions this employee can open in the hotel staff hub.')}
                                </p>
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        {isEditingEmployee && (
                            <span
                                data-testid="employee-dirty-indicator"
                                className={`text-xs font-medium ${isEmployeeDirty ? 'text-amber-700 dark:text-amber-300' : 'text-emerald-700 dark:text-emerald-300'}`}
                                style={{ marginRight: 'auto' }}
                            >
                                {isEmployeeDirty
                                    ? t('common.pendingChanges')
                                    : t('common.allSettingsSaved')}
                            </span>
                        )}
                        <Button variant="outline" onClick={() => setIsModalOpen(false)}>
                            {t('common.cancel')}
                        </Button>
                        <Button onClick={handleSubmit} data-testid="save-employee-btn">
                            {editingEmployee ? t('common.update') : t('common.create')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* PIN Modal */}
            <Dialog open={isPinModalOpen} onOpenChange={setIsPinModalOpen}>
                <DialogContent className="sm:max-w-[380px]" data-testid="pin-modal" data-insight-id="employees.workflow.pin-reset" data-insight-label="Set / Reset PIN">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <KeyRound className="w-5 h-5 text-orange-500" />
                            Set 4-Digit PIN
                        </DialogTitle>
                        {pinTarget && (
                            <p className="text-sm text-muted-foreground">
                                Setting PIN for <span className="font-medium text-foreground">{pinTarget.name}</span>
                            </p>
                        )}
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <div className="space-y-2">
                            <Label>{t('employees.enterPin')}</Label>
                            <Input
                                type="password"
                                inputMode="numeric"
                                maxLength={4}
                                value={pinValue}
                                onChange={(e) => setPinValue(e.target.value.replace(/\D/g, '').slice(0, 4))}
                                placeholder={t('employees.enterPin')}
                                data-testid="pin-input"
                                autoFocus
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>{t('employees.confirmPin')}</Label>
                            <Input
                                type="password"
                                inputMode="numeric"
                                maxLength={4}
                                value={pinConfirm}
                                onChange={(e) => setPinConfirm(e.target.value.replace(/\D/g, '').slice(0, 4))}
                                placeholder={t('employees.reenterPin')}
                                data-testid="pin-confirm-input"
                            />
                        </div>
                        {pinValue.length === 4 && pinConfirm.length === 4 && pinValue !== pinConfirm && (
                            <p className="text-xs text-red-500" data-testid="pin-mismatch-msg">{t('employees.pinsDontMatch')}</p>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsPinModalOpen(false)}>
                            {t('common.cancel')}
                        </Button>
                        <Button
                            onClick={handleSetPin}
                            disabled={pinSaving || pinValue.length !== 4 || pinValue !== pinConfirm}
                            data-testid="pin-save-btn"
                        >
                            {pinSaving ? t('common.saving') : t('common.save')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* v4.9.0 — MyTap Access Modal */}
            <MyTapAccessModal
                open={!!mytapEmployee}
                onClose={() => setMytapEmployee(null)}
                employee={mytapEmployee}
            />
        </div>
    );
};

export default Employees;
